// Components
import { ChartFilters } from "./components";
import CustomIcon from "components/CustomIcon";
import NavButton from "components/HeaderNavigation/NavButton";
import { Header, Body } from "components/Layout/LayoutContent";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade";
import { CardButton, LinkButton, ClearFilterButton } from "components/Buttons";

// MUI Components
import {
    Box,
    Button,
    FormControl,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Popover,
    Radio,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";

// Hooks
import { useIntl } from "react-intl";
import usePaceDashboard from "./usePaceDashboard";

// Styles
import PaceDashboardStyles from "./PaceDashboard.styles";

// Icons
import icons from "enums/icons";
import InfoIcon from "@mui/icons-material/Info";

const behaviouralSelectionOpts = [
    {
        id: 0,
        label: "High Performance Benchmark",
    },
    {
        id: 1,
        label: "Low Performance Benchmark",
    },
    {
        id: 2,
        label: "Finance Set Benchmark",
    },
];

export const PaceDashboard = () => {
    const intl = useIntl();
    const classes = PaceDashboardStyles();

    const {
        overallRef,
        boxplotRef,
        heatmapRef,
        hubAnalysisRef,
        engagementSummaryRef,
        activeSection,
        behaviouralSelectedValue,
        anchorEl,
        openLockMessage,
        lockMessageAnchorEl,
        handlePreFilteredRedirect,
        handleSelectClick,
        handleCloseSelect,
        handleBehaviouralChange,
        handleScrollTo,
        handleOpenLockedMessage,
        onCloseLockMessage,
    } = usePaceDashboard();

    return (
        <>
            <Header isSticky backgroundColor="primary">
                <Box className={classes.headerInner}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography className={classes.headerTitle}>PACE Dashboard</Typography>
                        <NavButton
                            variant="contained"
                            showBackArrow={false}
                            aria-label="Behavioural comparison"
                            onClick={() => console.log("clicked")}
                        >
                            Behavioural Comparison
                        </NavButton>
                    </Box>
                </Box>
            </Header>

            <Body>
                <Box className={classes.mainContainer}>
                    <Typography className={classes.pageDescription}>
                        {intl.formatMessage({ id: "paceDashboard.header.description" })}
                    </Typography>

                    {/* Engagement Summary Section */}
                    <Box className={classes.engagementHeader} ref={engagementSummaryRef}>
                        <Typography variant="h6" aria-label="Hub PACE Engagement Summary">
                            {intl.formatMessage({ id: "paceDashboard.sectionTitle.EngagementSummary" })}
                        </Typography>
                        <Box display="flex" alignItems="center">
                            <LinkButton label="PACE Portal" onClick={() => handlePreFilteredRedirect()} />
                            <CustomIcon icon={icons.hyperlink} className={classes.hiperLinkIcon} />
                        </Box>
                    </Box>

                    <Box className={classes.paceStatusButtonContainer}>
                        <CardButton icon={icons.paceIconColored} onClick={() => handlePreFilteredRedirect("Completed")}>
                            <Typography variant="subtitle1">Completed PACE: 30</Typography>
                        </CardButton>

                        <CardButton
                            icon={icons.paceIconNotShared}
                            onClick={() => handlePreFilteredRedirect("Not Sharing")}
                        >
                            <Typography variant="subtitle1">Not sharing PACE: 30</Typography>
                        </CardButton>

                        <CardButton
                            icon={icons.paceIconGrayedOut}
                            onClick={() => handlePreFilteredRedirect("Incomplete")}
                        >
                            <Typography variant="subtitle1">Incomplete/ not started PACE: 30</Typography>
                        </CardButton>
                    </Box>

                    {/* HUB Analysis */}
                    <Box
                        ref={hubAnalysisRef}
                        className={classes.hubAnalysisContainer}
                        sx={{
                            margin: activeSection !== "" ? "0px" : "0px 16px",
                            transition: "margin 0.1s ease-in-out",
                        }}
                    >
                        <Box>
                            <Typography variant="h6">Hub Analysis</Typography>
                            <Box display="flex" alignItems="center" mt={2}>
                                <Tooltip
                                    arrow
                                    placement="bottom-start"
                                    sx={{ color: "info.main", fontSize: 20 }}
                                    title={
                                        <Typography>
                                            {intl.formatMessage({ id: "paceDashboard.hubAnalysis.infoIcon.tooltip" })}
                                        </Typography>
                                    }
                                >
                                    <InfoIcon />
                                </Tooltip>
                                <Typography variant="subtitle2" ml={0.5} mr={1.5} color="text.secondary">
                                    Highlight key behaviours from:
                                </Typography>
                                <FormControl sx={{ width: 260 }}>
                                    <Select
                                        labelId="behavioural-select-labels"
                                        value={behaviouralSelectedValue}
                                        onOpen={handleSelectClick}
                                        open={Boolean(anchorEl)}
                                        displayEmpty
                                        onClose={handleCloseSelect}
                                        className={classes.selectField}
                                        IconComponent={(props) => (
                                            <CustomIcon
                                                icon={icons.chevronDownLight}
                                                {...props}
                                                sx={{ fontSize: 12 }}
                                            />
                                        )}
                                        MenuProps={{
                                            PaperProps: {
                                                className: classes.selectBehaviourPaper,
                                            },
                                        }}
                                        renderValue={(selected) => selected || "None Selected"}
                                    >
                                        {behaviouralSelectionOpts.map((option) => (
                                            <MenuItem
                                                key={option.id}
                                                onClick={() => handleBehaviouralChange(option.label)}
                                            >
                                                <ListItemIcon>
                                                    <Radio
                                                        size="small"
                                                        checked={behaviouralSelectedValue === option.label}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary={option.label} />
                                            </MenuItem>
                                        ))}

                                        <Box display="flex" justifyContent="flex-end">
                                            <ClearFilterButton
                                                isDisabled={behaviouralSelectedValue === ""}
                                                onClick={() => handleBehaviouralChange("")}
                                            />
                                        </Box>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box>
                            <Button
                                variant={activeSection === "overall" || activeSection === "" ? "text" : "outlined"}
                                size="small"
                                sx={{ marginRight: "16px" }}
                                onClick={() => handleScrollTo(overallRef)}
                            >
                                <Typography>Overall Analysis</Typography>
                            </Button>
                            <Button
                                variant={activeSection === "boxplot" ? "text" : "outlined"}
                                size="small"
                                sx={{ marginRight: "16px" }}
                                onClick={() => handleScrollTo(boxplotRef)}
                            >
                                <Typography>Box Plot Analysis</Typography>
                            </Button>
                            <Button
                                variant={activeSection === "heatmap" ? "text" : "outlined"}
                                size="small"
                                onClick={() => handleScrollTo(heatmapRef)}
                            >
                                <Typography>Heatmap Comparison</Typography>
                            </Button>
                        </Box>
                    </Box>

                    {/* Charts Section */}
                    <Box sx={{ padding: "16px 16px" }}>
                        {/* Overall Analysis Section */}
                        <Box ref={overallRef} height={750}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">Overall Analysis</Typography>
                                <Box display="flex" alignItems="center">
                                    <LinkButton
                                        label={intl.formatMessage({ id: "paceDashboard.benchmarkLink.label" })}
                                        onClick={handleOpenLockedMessage}
                                    />
                                    <CustomIcon
                                        icon={icons.lockFeature}
                                        sx={{ marginLeft: "4px", color: "text.lightBlue" }}
                                    />
                                </Box>
                            </Box>
                            Overall Analysis
                            <ChartFilters id={"Overall"} />
                        </Box>

                        {/* Box Plot Analysis Section */}
                        <Box ref={boxplotRef} height={750}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">Box Plot Analysis</Typography>
                                <Box display="flex" alignItems="center">
                                    <LinkButton
                                        label={intl.formatMessage({ id: "paceDashboard.benchmarkLink.label" })}
                                        onClick={handleOpenLockedMessage}
                                    />
                                    <CustomIcon
                                        icon={icons.lockFeature}
                                        sx={{ marginLeft: "4px", color: "text.lightBlue" }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Heatmap Comparison Section */}
                        <Box ref={heatmapRef} height={750}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">Heatmap Comparison</Typography>
                                <Box display="flex" alignItems="center">
                                    <LinkButton
                                        label={intl.formatMessage({ id: "paceDashboard.benchmarkLink.label" })}
                                        onClick={handleOpenLockedMessage}
                                    />
                                    <CustomIcon
                                        icon={icons.lockFeature}
                                        sx={{ marginLeft: "4px", color: "text.lightBlue" }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {openLockMessage && (
                    <Popover
                        open={openLockMessage}
                        anchorEl={lockMessageAnchorEl}
                        onClose={onCloseLockMessage}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        PaperProps={{ sx: { width: "100%", maxWidth: 480 } }}
                    >
                        <MessageUpgrade
                            variant="type3"
                            width="100%"
                            icon={icons.lockFeature}
                            title={"paceDashboard.unlockBenchmark.title"}
                            subtitle={"paceDashboard.unlockBenchmark.subtitle"}
                            descriptionTitle={"paceDashboard.unlockBenchmark.descriptionTitle"}
                            onClose={onCloseLockMessage}
                        >
                            Benchmark Comparison allows you to focus on the key behaviors exhibited by top performers,
                            helping to identify the behavioral culture that fosters team success and drives growth
                            within the business.
                        </MessageUpgrade>
                    </Popover>
                )}
            </Body>
        </>
    );
};
